import React, { forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import GoogleAutocomplete from 'react-google-autocomplete';
import useGoogleMapsReady from 'src/hooks/useGoogleMapsReady';
import { GOOGLE_MAPS_API_KEY } from 'src/utils/googleMaps';

/**
 * Google Places address field.
 *
 * Owns two things call sites should never repeat:
 *
 * 1. The credential. Sourced from VITE_GOOGLE_MAPS_API_KEY via
 *    src/utils/googleMaps.js — no call site passes a key, so none can drift.
 *
 * 2. Places readiness. react-google-autocomplete wires the widget in a
 *    mount-time effect and gives up permanently if google.maps.places is not
 *    there yet:
 *
 *      if (!google.maps?.places)
 *        return console.error("Google maps places API must be loaded.");
 *
 *    The app shell loads Maps with `loading=async`, so on a cold page load
 *    `google` exists well before `places` does. The widget therefore mounted
 *    too early and silently never attached — autocomplete that "works
 *    sometimes", depending on cache warmth. Keying the child on readiness
 *    remounts it exactly once, at the moment Places is genuinely available.
 *
 * Renders a plain <input> either way, so a missing key or a failed script
 * degrades to a normal typable address field rather than a dead one. Every
 * unrecognised prop (style, placeholder, className, disabled, onChange, …) is
 * forwarded to that input by the underlying component.
 */
const AddressAutocomplete = forwardRef(({ onChange, defaultValue, ...rest }, ref) => {
    const { isLoaded } = useGoogleMapsReady('places');

    // Carries anything typed before Places landed across the single remount, so
    // the cold-load case never loses a partially entered address.
    const typedRef = useRef(undefined);

    const handleChange = (event) => {
        typedRef.current = event?.target?.value;
        if (onChange) onChange(event);
    };

    return (
        <GoogleAutocomplete
            // Remount once, when Places becomes available, so the widget's
            // mount-time effect runs against a namespace that has it.
            key={isLoaded ? 'places-ready' : 'places-pending'}
            ref={ref}
            apiKey={GOOGLE_MAPS_API_KEY}
            defaultValue={typedRef.current !== undefined ? typedRef.current : defaultValue}
            onChange={handleChange}
            {...rest}
        />
    );
});

AddressAutocomplete.propTypes = {
    onChange: PropTypes.func,
    defaultValue: PropTypes.string,
    onPlaceSelected: PropTypes.func,
    options: PropTypes.object,
};

export default AddressAutocomplete;
